/**
 * A roster entry as a file ROW — what the list can draw, and a bulk write can
 * act on, before the heavy per-row query has reached that file (VE-32420).
 *
 * The roster indexes every file in a request cheaply. A row built from one
 * carries everything the list sorts, filters and selects by — name, the whole
 * `details` blob, both dates, duration and whether a redacted output exists —
 * and nothing else. The fields that need the heavy query are simply ABSENT,
 * which is the point: a column whose data has not arrived can tell that it has
 * not arrived and say so, rather than drawing a confident wrong answer.
 *
 * WHAT IS DELIBERATELY MISSING, and why it has to stay missing:
 *
 * - `redactedMediaAssets`. Absent means "not loaded", never "none". Several
 *   callers take `records[0]` and use the asset — its id, its signed uri, its
 *   size — so a synthesized list could put an asset nothing ever fetched into
 *   a download. Existence questions go through `fileHasRedactedOutput`, which
 *   reads the boolean below instead (see `redactedOutput`).
 * - `tasks` / `jobs`. The processing-state dot. Measured as the expensive part
 *   of the heavy query, so it is fetched for rows on screen, not for all of
 *   them.
 * - `thumbnailUrl`, `primaryAsset`, `streams`. Only rows on screen need them.
 * - `folders`. The request's own chain, identical for every file in it, so the
 *   soft delete rebuilds it once from the breadcrumb (`folderChainToPath`)
 *   instead of carrying a copy on every row. A write that needs it must use
 *   that fallback: with no path the file is ADDED to Trash rather than moved
 *   into it, and stays filed under the request as well.
 *
 * Shared by the list and by `selectSelectedTdoFile`, so a row the user can see
 * and a row a write acts on are built the same way.
 */
import type { AssetFileType, RequestRosterEntry } from './store';

/**
 * True when this row came from the roster rather than the heavy query — i.e.
 * its per-row columns are still loading. The list reads this to decide which
 * cells show a spinner.
 */
export const isRosterOnlyRow = (file: unknown): boolean => {
  // Deliberately structural, and deliberately `unknown`: the row types that
  // reach this (loaded row, roster row, legacy media) share no common base,
  // and casting each one to `AssetFileType` to ask the question is the move
  // that hid a whole class of bug behind a green compile.
  //
  // THE COROLLARY callers must respect: a row from some other source carrying
  // neither field answers TRUE. An unassigned file is the live example — it is
  // not a roster row, so a caller that can see one excludes it itself.
  if (!file || typeof file !== 'object') {
    return false;
  }
  const row = file as { redactedMediaAssets?: unknown; tasks?: unknown };
  return row.redactedMediaAssets === undefined && row.tasks === undefined;
};

export const rosterEntryToFile = (entry: RequestRosterEntry): AssetFileType =>
  ({
    id: entry.id,
    name: entry.name,
    details: entry.details,
    createdDateTime: entry.createdDateTime,
    modifiedDateTime: entry.modifiedDateTime,
    startDateTime: entry.startDateTime,
    stopDateTime: entry.stopDateTime,
    status: entry.status,
    hasRedactedOutput: entry.hasRedactedOutput,
  }) as unknown as AssetFileType;

/**
 * The request's files in roster order, each one the LOADED row where the heavy
 * query has reached it and a roster row where it has not.
 *
 * Roster order, not loaded order: the roster is the complete set, so it decides
 * which files exist and the loaded rows only enrich them. A file in the loaded
 * pages but not the roster has been filtered out of the request (trashed,
 * export, project) and is correctly dropped.
 */
export const mergeRosterWithLoaded = (
  entries: readonly RequestRosterEntry[],
  loaded: readonly AssetFileType[]
): AssetFileType[] => {
  const loadedById = new Map(loaded.map((file) => [String(file.id), file]));
  return entries.map(
    (entry) => loadedById.get(String(entry.id)) ?? rosterEntryToFile(entry)
  );
};
